import {
  Box,
  Button,
  CardActions,
  CardContent,
  CardHeader,
  CircularProgress,
  Divider,
  IconButton,
  Modal,
  SvgIcon,
  Tooltip,
  Typography,
} from "@mui/material";
import { Info } from "@mui/icons-material";
import React, { PropsWithChildren, useState, ReactNode } from "react";
import { logger } from "../../../services/logger";
import CloseIcon from "@mui/icons-material/Close";
import Card from "@mui/material/Card";
import { makeStyles } from "@mui/styles";
import { generateGtmEvent } from "../../../Helpers";

type UserManagementModalProps = PropsWithChildren<{
  open: boolean;
  onClose: () => void;
  title: string;
  titleElem?: string | ReactNode;
  onSave?: () => Promise<any>;
  errorText?: string;
  disableSubmit?: boolean;
  hideSubmitButton?: boolean;
}>;
const useStyle = makeStyles((theme) => ({
  userManagementModal: {
    position: "absolute",
    width: "min(900px, calc(100vw - 32px))",
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[5],
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
  },
  modalTitle: {
    color: theme.palette.text.primary,
    fontWeight: theme.typography.fontWeightBold,
    fontSize: "20px",
  },
  closeButton: {
    color: theme.palette.text.secondary,
  },
}));

const HandleDataLayer = (
  event: string,
  action: string,
  name: string,
  type: string,
  region: string,
  section: string,
  text: string,
  metaData?: any
) => {
  generateGtmEvent({ event, action, name, type, region, section, text }, metaData);
};

export function UserManagementModal(props: UserManagementModalProps) {
  const [loading, setLoading] = useState(false);
  const { titleElem = props.title, hideSubmitButton = false } = props;
  const classes = useStyle();

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = async (e) => {
    e.preventDefault();
    if (props.onSave !== undefined) {
      try {
        setLoading(true);
        const result = await props.onSave();

        // Close modal unless explicitly told NOT to
        if (result !== false) {
          props.onClose();
        }
      } catch (e) {
        logger.error(e);
      } finally {
        setLoading(false);
      }
    }
  };
  return (
    <>
      <Modal open={props.open} onClose={props.onClose}>
        <Card className={classes.userManagementModal}>
          <CardHeader
            sx={{ padding: "16px 24px" }}
            title={
              <Typography
                id="edit-account-modal-title"
                variant="h6"
                className={classes.modalTitle}>
                {titleElem}
              </Typography>
            }
            action={
              <Tooltip
                placement="bottom-end"
                title="Close"
                arrow
                role="tooltip"
                describeChild>
                <IconButton
                  onClick={() => {
                    HandleDataLayer(
                      "modal",
                      "close",
                      "onclick",
                      "click",
                      "main content",
                      props.title,
                      "close cross"
                    );
                    props.onClose();
                  }}
                  aria-label="close edit user account"
                  className={classes.closeButton}>
                  <CloseIcon />
                </IconButton>
              </Tooltip>
            }
          />
          <Divider />
          <CardContent
            sx={{
              padding: "24px 24px 24px 32px",
              maxHeight: "400px",
              overflow: "auto",
              scrollbarGutter: "stable",
            }}>
            <form id="user-management-form" onSubmit={handleSubmit}>
              {props.children}
            </form>
          </CardContent>
          {!hideSubmitButton && (
            <>
              <Divider />
              <CardActions sx={{ padding: "24px 32px" }}>
                <Box
                  component="span"
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    marginRight: "16px",
                  }}
                  role="alert">
                  {props.errorText && (
                    <>
                      <SvgIcon color="error" sx={{ marginRight: 4 }}>
                        <Info />
                      </SvgIcon>
                      <Typography color="error">{props.errorText}</Typography>
                    </>
                  )}
                </Box>
                <Button
                  type="submit"
                  form="user-management-form"
                  variant="contained"
                  size="large"
                  disabled={loading || !!props.errorText || props.disableSubmit}
                  sx={{
                    marginLeft: "auto",
                    marginRight: 0,
                  }}
                  disableElevation>
                  {loading ? <CircularProgress /> : "Save"}
                </Button>
              </CardActions>{" "}
            </>
          )}
        </Card>
      </Modal>
    </>
  );
}
